/**
 * One person, and the actions their state actually allows.
 *
 * The menu is built from what is true about them, not from a fixed list with
 * things greyed out. Somebody who never accepted has no history, so their
 * record can be corrected or removed entirely; somebody who accepted has work
 * hanging off their id, so the only honest options are amendments. Offering
 * "Delete" to the second group and failing would be worse than not offering it,
 * which is why the disabled entry says why in the one line it has.
 */

import { useState } from 'react';
import {
  BellRing,
  Link2,
  Mail,
  Pencil,
  Send,
  ShieldAlert,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS, SYSTEM_ROLES, type SystemRole } from '@virtuetasks/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/avatar';
import { UserLink } from '@/components/shared/UserLink';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { describeError } from '@/lib/errorMessage';
import { useAuth, useCan } from '@/features/auth';
import {
  useCancelInvite,
  useInviteLink,
  useResendInvite,
  useSetUserActive,
  useUpdateRole,
  type InvitationSummary,
  type PersonView,
} from '../api';
import { deliveryNote, inviteLine } from './inviteMeta';
import { EditEmailDialog } from './EditEmailDialog';
import { EditProfileDialog } from './EditProfileDialog';
import { ReassignWorkDialog } from './ReassignWorkDialog';

const ROLE_TONE: Record<SystemRole, 'accent' | 'neutral'> = {
  owner: 'accent',
  admin: 'accent',
  manager: 'neutral',
  member: 'neutral',
  guest: 'neutral',
};

const DELETE_EXPLANATION = 'Members with history are deactivated, not deleted';

function humanise(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const DELIVERY_CLASS: Record<'ok' | 'warn' | 'danger', string> = {
  ok: 'vt-muted',
  warn: 'text-[var(--warn)]',
  danger: 'text-[var(--danger)]',
};

export function MemberRow({
  person,
  invitation,
  ownerId,
}: {
  person: PersonView;
  invitation?: InvitationSummary;
  ownerId?: string;
}) {
  const can = useCan();
  const { user: me } = useAuth();
  const updateRole = useUpdateRole();
  const setActive = useSetUserActive();
  const resend = useResendInvite();
  const inviteLink = useInviteLink();
  const cancel = useCancelInvite();

  const [editingProfile, setEditingProfile] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [reassigning, setReassigning] = useState(false);

  const isOwner = person.systemRole === 'owner' || person._id === ownerId;
  const isSelf = person._id === me?._id;
  const canManageRole = can(PERMISSIONS.USER_UPDATE_ROLE) && !isOwner && !isSelf;
  const canDeactivate = can(PERMISSIONS.USER_DEACTIVATE) && !isOwner && !isSelf;
  const canInvite = can(PERMISSIONS.USER_INVITE);
  const canEditProfile = can(PERMISSIONS.USER_UPDATE);

  /**
   * Never accepted — the placeholder case.
   *
   * Read from the row rather than from `status` alone: a suspended invitee is
   * still an invitee, and their invitation is still the thing to fix or cancel.
   */
  const neverAccepted = person.status === 'invited' || Boolean(invitation?.status);

  async function changeRole(systemRole: SystemRole) {
    try {
      await updateRole.mutateAsync({ userId: person._id, systemRole });
      toast.success(`${person.name} is now ${humanise(systemRole)}`);
    } catch (err) {
      toast.error(describeError(err, 'Could not change the role'));
    }
  }

  async function toggleActive() {
    const activating = person.status === 'suspended';
    try {
      const result = await setActive.mutateAsync({ userId: person._id, active: activating });
      if (activating) {
        // The server decides what they go back to, and it is not always active.
        const restored = result.user.status;
        toast.success(
          restored === 'invited'
            ? `${person.name} is invited again — they still need to accept`
            : `${person.name} reactivated`,
        );
      } else if (result.openTaskCount) {
        toast.warning(
          `${person.name} deactivated — ${result.openTaskCount} open ${result.openTaskCount === 1 ? 'task is' : 'tasks are'} still assigned to them.`,
        );
      } else {
        toast.success(`${person.name} deactivated`);
      }
    } catch (err) {
      toast.error(describeError(err, 'Could not change the status'));
    }
  }

  async function copyInviteLink() {
    try {
      const { url } = await inviteLink.mutateAsync(person._id);
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied — it works even if email does not', { duration: 8_000 });
    } catch (err) {
      toast.error(describeError(err, 'Could not produce an invite link'));
    }
  }

  async function resendInvite() {
    try {
      await resend.mutateAsync(person._id);
      toast.success(`A new invitation is on its way to ${person.email}`);
    } catch (err) {
      toast.error(describeError(err, 'Could not resend the invitation'));
    }
  }

  async function cancelInvite() {
    try {
      const result = await cancel.mutateAsync(person._id);
      toast.success(`Invitation to ${result.email} cancelled`);
    } catch (err) {
      // The refusal names what they own and what to do instead, so it is worth
      // reading — a two-second toast is not long enough for that.
      toast.error(describeError(err, 'Could not cancel the invitation'), { duration: 15_000 });
    }
  }

  const delivery = deliveryNote(invitation);

  return (
    <tr className="border-b border-[var(--border)] last:border-b-0" data-testid={`member-row-${person.email}`}>
      <td className="py-2 pl-3 pr-2">
        <div className="flex items-center gap-2.5">
          <UserAvatar id={person._id} name={person.name} src={person.avatarUrl} size={26} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              {/* V5-08: the name is the way into their profile. */}
              <UserLink
                userId={person._id}
                name={person.name}
                avatarUrl={person.avatarUrl}
                nameOnly
                showMessage
                className="truncate font-medium"
              />
              {person.twoFactorEnabled && (
                <span title="Two-factor enabled" className="text-[var(--accent)]">
                  <ShieldAlert className="size-3" />
                </span>
              )}
            </div>
            <div className="truncate text-[11px] vt-muted">{person.email}</div>
          </div>
        </div>
      </td>
      <td className="px-2 py-2">
        <Badge tone={ROLE_TONE[person.systemRole]}>{humanise(person.systemRole)}</Badge>
      </td>
      <td className="px-2 py-2 vt-muted">{humanise(person.designation)}</td>
      <td className="vt-mono px-2 py-2 text-right vt-muted">{person.capacityHoursPerWeek}h</td>
      {/* V7-01 §2.3 — "did anyone actually enable this?" as a glance rather
          than a survey. A count of browsers, never an endpoint. */}
      <td className="px-2 py-2 text-center" data-testid="member-push" data-devices={person.pushDevices ?? 0}>
        {person.pushDevices ? (
          <span
            className="inline-flex items-center gap-1 text-[12px] text-[var(--accent)]"
            title={`Push enabled on ${person.pushDevices} browser${person.pushDevices === 1 ? '' : 's'}`}
          >
            <BellRing className="size-3.5" aria-hidden />
            {person.pushDevices}
          </span>
        ) : (
          <span className="text-[12px] vt-muted" title="No browser has push enabled for this person">
            —
          </span>
        )}
      </td>
      <td className="px-2 py-2" data-testid="member-status">
        {person.status === 'active' ? (
          <Badge tone="accent">Active</Badge>
        ) : person.status === 'invited' ? (
          <div className="flex flex-col gap-0.5">
            <Badge tone="warn">Invited</Badge>
            <span className="text-[11px] vt-muted" data-testid={`invite-line-${person.email}`}>
              {inviteLine(invitation)}
            </span>
            <span
              className={`text-[11px] ${DELIVERY_CLASS[delivery.tone]}`}
              data-testid={`invite-delivery-${person.email}`}
              title={delivery.detail ?? undefined}
            >
              {delivery.label}
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            <Badge tone="danger">Deactivated</Badge>
            {neverAccepted && (
              <span className="text-[11px] vt-muted">never accepted their invitation</span>
            )}
          </div>
        )}
      </td>
      <td className="py-2 pl-2 pr-3 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${person.name}`}>
              ⋯
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {neverAccepted ? (
              <>
                <DropdownMenuLabel>Pending invitation</DropdownMenuLabel>
                {canInvite && (
                  <>
                    <DropdownMenuItem
                      data-testid="edit-invite-email"
                      onSelect={() => setEditingEmail(true)}
                    >
                      <Mail /> Edit email
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="resend-invite" onSelect={() => void resendInvite()}>
                      <Send /> Resend invitation
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="copy-invite-link" onSelect={() => void copyInviteLink()}>
                      <Link2 /> Copy invite link
                    </DropdownMenuItem>
                  </>
                )}
                {canDeactivate && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      destructive
                      data-testid="cancel-invite"
                      onSelect={() => void cancelInvite()}
                    >
                      <Trash2 /> Cancel invitation
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => void toggleActive()}>
                      {person.status === 'suspended' ? (
                        <>
                          <UserPlus /> Reactivate
                        </>
                      ) : (
                        <>
                          <UserMinus /> Deactivate
                        </>
                      )}
                    </DropdownMenuItem>
                  </>
                )}
              </>
            ) : (
              <>
                <DropdownMenuLabel>Member</DropdownMenuLabel>
                {canEditProfile && (
                  <DropdownMenuItem
                    data-testid="edit-profile"
                    onSelect={() => setEditingProfile(true)}
                  >
                    <Pencil /> Edit profile
                  </DropdownMenuItem>
                )}
                {canDeactivate && (
                  <DropdownMenuItem
                    data-testid="change-member-email"
                    onSelect={() => setEditingEmail(true)}
                  >
                    <Mail /> Change email
                  </DropdownMenuItem>
                )}
                {canManageRole && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Change role</DropdownMenuLabel>
                    {SYSTEM_ROLES.filter((r) => r !== 'owner' && r !== person.systemRole).map((r) => (
                      <DropdownMenuItem key={r} onSelect={() => void changeRole(r)}>
                        {humanise(r)}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                {canDeactivate && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="reassign-work" onSelect={() => setReassigning(true)}>
                      <Users /> Reassign work
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      destructive={person.status !== 'suspended'}
                      data-testid="toggle-active"
                      onSelect={() => void toggleActive()}
                    >
                      {person.status === 'suspended' ? (
                        <>
                          <UserPlus /> Reactivate
                        </>
                      ) : (
                        <>
                          <UserMinus /> Deactivate
                        </>
                      )}
                    </DropdownMenuItem>
                    {/* Disabled items swallow their own pointer events, so the
                        explanation hangs on the wrapper — otherwise hovering the
                        one action people will reach for tells them nothing. */}
                    <span title={DELETE_EXPLANATION} data-testid="delete-disabled">
                      <DropdownMenuItem disabled onSelect={(e) => e.preventDefault()}>
                        <Trash2 /> Delete
                      </DropdownMenuItem>
                    </span>
                    <p className="px-2 pb-1 pt-0.5 text-[11px] vt-muted">{DELETE_EXPLANATION}.</p>
                  </>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>

      {/* Dialogs portal out of the table, but React still needs a legal parent
          inside a <tr> — hence one hidden cell rather than three. */}
      <td className="hidden">
        {editingProfile && (
          <EditProfileDialog
            person={person}
            canChangeRole={canManageRole}
            open={editingProfile}
            onOpenChange={setEditingProfile}
          />
        )}
        {editingEmail && (
          <EditEmailDialog
            person={person}
            mode={neverAccepted ? 'invite' : 'member'}
            open={editingEmail}
            onOpenChange={setEditingEmail}
          />
        )}
        {reassigning && (
          <ReassignWorkDialog person={person} open={reassigning} onOpenChange={setReassigning} />
        )}
      </td>
    </tr>
  );
}
